import { useState, useRef } from "react";
import { auth } from "../../firebase";
import {
  signInWithEmailAndPassword,
  signInWithPopup,
  GoogleAuthProvider,
  sendPasswordResetEmail,
} from "firebase/auth";
import "./signIn.css";
import GoogleButton from "react-google-button";
import { MdLogin } from "react-icons/md";
import { useNavigate } from "react-router-dom";
import LoadingSpinner from "../LoadingSpinner/LoadingSpinner";

const SignIn = () => {
  const [email, setEmail] = useState<string>("");
  const [password, setPassword] = useState<string>("");
  const [error, setError] = useState<string>("");
  const [loading, setLoading] = useState<boolean>(false);
  const emailRef = useRef<HTMLInputElement>(null);
  const passwordRef = useRef<HTMLInputElement>(null);
  const navigate = useNavigate();

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setLoading(true);
    try {
      const userCredential = await signInWithEmailAndPassword(
        auth,
        email,
        password
      );

      // Check if email is verified
      if (!userCredential.user.emailVerified) {
        setError(
          "Please verify your email before signing in. Check your inbox for the verification link."
        );
        await auth.signOut(); // Sign out the unverified user
        return;
      }

      navigate("/dashboard");
    } catch (error: any) {
      const errorCode = error.code;
      handleError(errorCode);
    } finally {
      setLoading(false);
    }
  };

  const handleError = (errorCode: string) => {
    switch (errorCode) {
      case "auth/invalid-credential":
        passwordRef.current?.focus();
        setError("Invalid email/password combination");
        break;
      case "auth/invalid-email":
        emailRef.current?.focus();
        setError("Please enter a valid email address");
        break;
      case "auth/missing-password":
        passwordRef.current?.focus();
        setError("Please enter a valid password");
        break;
      case "auth/wrong-password":
        passwordRef.current?.focus();
        setError("Invalid email/password combination");
        break;
      case "auth/too-many-requests":
        setError("Too many requests, please try again later");
        break;
      case "auth/user-not-found":
        setError("No account found with this email");
        break;
      default:
        setError("An error has occurred");
        break;
    }
  };

  const signInWithGoogle = async (e: React.FormEvent) => {
    e.preventDefault();
    setLoading(true);
    try {
      const provider = new GoogleAuthProvider();
      await signInWithPopup(auth, provider);
      navigate("/dashboard");
    } catch (err: any) {
      handleError(err.code);
    } finally {
      setLoading(false);
    }
  };

  const handleForgotPassword = async () => {
    try {
      await sendPasswordResetEmail(auth, email);
      setError("Password reset email sent. Please check your inbox.");
    } catch (err) {
      setError("Failed to send password reset email");
    }
  };

  return (
    <div className="auth" role="main">
      {loading && (
        <div className="auth__loading" role="status" aria-label="Loading">
          <LoadingSpinner />
        </div>
      )}
      <form
        onSubmit={handleSubmit}
        className="auth__form"
        aria-labelledby="login-heading"
      >
        <h1 className="auth__form__header">Log In</h1>
        <input
          type="email"
          placeholder="Enter your Email"
          value={email}
          onChange={(e) => setEmail(e.target.value)}
          className="auth__form__input"
          ref={emailRef}
          aria-required="true"
          aria-invalid={error ? "true" : "false"}
          aria-describedby={error ? "error-message" : undefined}
        />

        <input
          type="password"
          placeholder="Enter your Password"
          value={password}
          onChange={(e) => setPassword(e.target.value)}
          className="auth__form__input"
          ref={passwordRef}
          aria-required="true"
          aria-invalid={error ? "true" : "false"}
          aria-describedby={error ? "error-message" : undefined}
        />

        {error && (
          <p className="auth__form__error" id="error-message" role="alert">
            {error}
          </p>
        )}

        <div className="auth__form__buttonGroup">
          <button type="submit" className="auth__form__button">
            <MdLogin className="bttn-icon" />
            Log In
          </button>
          <GoogleButton
            onClick={signInWithGoogle}
            className="auth__form__google"
          />
        </div>

        <p className="auth__form__disclaimer">
          By signing in, you agree to our Terms and Conditions & Privacy Policy
        </p>

        <div className="auth__form__links">
          <button
            className="auth__form__forgotPass"
            onClick={handleForgotPassword}
            aria-label="Reset your password"
          >
            Forgot Password
          </button>
          <button
            className="auth__form__forgotPass"
            aria-label="Create a new account"
            onClick={() => navigate("/signup")}
          >
            Sign Up
          </button>
        </div>
      </form>
    </div>
  );
};

export default SignIn;
